/**
 * Token-Verifikation: wird direkt als GET-Redirect vom Backend ausgelöst.
 * Diese Seite fängt Fehlerparameter ab, die das Backend per Query-String signalisiert.
 * Backend handled alle Kontakte automatisch – nur ein Magic Link pro Firma.
 *
 * Auch Ziel der Mitarbeiter-Ansicht (`?vorschau=1`): Das Backend hat die Sitzung schon
 * gesetzt; hier wird /me frisch geladen, denn der gespeicherte Kontakt kann aus einer
 * früheren Sitzung stammen — sonst stünde kurz der falsche Name im Portal.
 */
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Center, Loader, Stack, Text, Alert, Button } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { getCustomerMe } from '../../lib/api.customer';
import { useCustomerStore } from '../../stores/customerStore';

const ERROR_MESSAGES: Record<string, string> = {
  missing_token: 'Der Anmelde-Link ist unvollständig.',
  invalid_token: 'Dieser Link ist abgelaufen oder wurde bereits verwendet. Bitte fordere einen neuen an.',
  account_inactive: 'Dein Konto ist aktuell deaktiviert. Bitte wende dich an den Support.',
  preview_invalid:
    'Diese Mitarbeiter-Ansicht ist abgelaufen oder wurde bereits geöffnet. Bitte im ServiceHub beim Ansprechpartner erneut „Portal ansehen" wählen.',
};

export function CustomerVerifyPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setContact = useCustomerStore((s) => s.setContact);
  const setLoading = useCustomerStore((s) => s.setLoading);

  const error = params.get('error');
  const isPreview = params.get('vorschau') === '1';
  const isPreviewError = error === 'preview_invalid';

  useEffect(() => {
    if (error) return;

    // Wenn kein Fehler: Backend hat bereits Cookie gesetzt und redirect zu /customer/dashboard.
    // Diese Seite wird nur gezeigt, wenn das Frontend-Routing den Request abfängt.
    // Versuch, /api/customer/me aufzurufen und bei Erfolg zum Dashboard weiterleiten.
    setLoading(true);
    getCustomerMe()
      .then((contact) => {
        setContact(contact);
        navigate('/customer/dashboard', { replace: true });
      })
      .catch(() => {
        navigate(isPreview ? '/customer/verify?error=preview_invalid' : '/customer/login?error=invalid_token', {
          replace: true,
        });
      })
      .finally(() => setLoading(false));
  }, [error, isPreview, navigate, setContact, setLoading]);

  if (error) {
    return (
      <Center h="100dvh">
        <Stack align="center" maw={400} p="xl">
          <Alert
            icon={<IconAlertCircle size={20} />}
            color="red"
            radius="md"
            title={isPreviewError ? 'Mitarbeiter-Ansicht nicht verfügbar' : 'Anmeldung fehlgeschlagen'}
          >
            {ERROR_MESSAGES[error] ?? 'Ein unbekannter Fehler ist aufgetreten.'}
          </Alert>
          {!isPreviewError && (
            <Button variant="light" color="orange" onClick={() => navigate('/customer/login')}>
              Neuen Link anfordern
            </Button>
          )}
        </Stack>
      </Center>
    );
  }

  return (
    <Center h="100dvh">
      <Stack align="center" gap="md">
        <Loader size="md" color="orange" />
        <Text c="dimmed" size="sm">
          {isPreview ? 'Mitarbeiter-Ansicht wird geöffnet …' : 'Anmeldung wird abgeschlossen …'}
        </Text>
      </Stack>
    </Center>
  );
}
